import { useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

export const PasteReplyDialog = ({ rec, onClose, onDone }) => {
  const [from, setFrom] = useState(rec?.email || '');
  const [body, setBody] = useState('');
  const go = async () => {
    try {
      const r = await api.post(`/api/bulk/recipients/${rec.id}/paste-reply`, { from, body });
      const msg = { new: 'Director ID found – sent to Responses for review', multiple: 'Several numbers found – choose one in Responses', conflict: 'Differs from the ID on file – review in Responses',
        no_number: 'No Director ID found – reply logged, request stays open', invalid_format: 'Number found but not a valid Director ID (must start 036) – request stays open',
        sender_not_authorised: "Sender doesn't match the director's email – sent to Responses, nothing applied", matches_existing: 'Matches the ID on file – marked complete' }[r.data.result];
      toast.success(msg || 'Reply processed');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not process reply'); }
  };
  return (
    <Dialog open={!!rec} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="paste-reply-dialog">
        <DialogHeader><DialogTitle>Paste reply – {rec?.director}</DialogTitle></DialogHeader>
        <Input placeholder="Sender email address" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="paste-reply-from" />
        <Textarea rows={8} placeholder="Paste the email reply text" value={body} onChange={(e) => setBody(e.target.value)} data-testid="paste-reply-body" />
        <p className="text-xs text-muted-foreground">Matching is deterministic (15-digit numbers starting 036). Nothing is applied without staff approval.</p>
        <Button onClick={go} disabled={!from || !body} data-testid="paste-reply-submit">Process reply</Button>
      </DialogContent>
    </Dialog>
  );
};

const OPTS = { scope: ['completed', 'all', 'awaiting_review', 'not_previously_exported', 'selected'], fmt_: ['xlsx', 'csv'], layout: ['header', 'template'],
  acn_format: ['spaced', 'plain'], did_format: ['plain', 'spaced'], name_format: ['first_middle_last', 'first_last'] };
const LBL = { header: 'Header row only', template: 'Replicate template rows 1–3', spaced: 'Spaced', plain: 'Plain', first_last: 'First and last name only (drops middle names)', first_middle_last: 'Full legal name as stored (First Middle Last)' };

export const ExportDialog = ({ open, onClose, campaignId, selected, isAdmin }) => {
  const [o, setO] = useState({ scope: 'completed', fmt_: 'xlsx', layout: 'header', acn_format: 'spaced', did_format: 'plain', name_format: 'first_middle_last', bom: false, full: false });
  const [pv, setPv] = useState(null);
  const params = () => ({ ...o, campaign_id: campaignId, recipient_ids: o.scope === 'selected' ? selected.join(',') : undefined });
  const preview = async () => { try { setPv((await api.get('/api/bulk/export', { params: { ...params(), preview: true } })).data); } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); } };
  const download = async () => {
    try {
      const r = await api.get('/api/bulk/export', { params: params(), responseType: 'blob' });
      const name = (r.headers['content-disposition'] || '').match(/filename="([^"]+)"/)?.[1] || `export.${o.fmt_}`;
      const a = document.createElement('a'); a.href = URL.createObjectURL(r.data); a.download = name; a.click();
      toast.success(`${r.headers['x-export-rows'] || ''} row(s) exported – ${r.headers['x-export-label'] || ''}`);
    } catch (e) { toast.error('Export failed (full Director IDs need owner/admin + re-confirming your password)'); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="export-dialog">
        <DialogHeader><DialogTitle>Export responses – NowInfinity Director ID</DialogTitle></DialogHeader>
        <p className="text-xs rounded bg-amber-500/10 text-amber-700 dark:text-amber-300 px-2 py-1" data-testid="export-not-validated-label">Not yet validated for NowInfinity – mark validated in Campaign Reports after a confirmed import.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Object.entries(OPTS).map(([k, vals]) => (
            <label key={k} className="text-xs text-muted-foreground">{k.replace('_', ' ').replace('fmt ', 'format')}
              <select className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground" value={o[k]} onChange={(e) => setO({ ...o, [k]: e.target.value })} data-testid={`export-opt-${k}`}>
                {vals.map((v) => <option key={v} value={v}>{LBL[v] || v.replace(/_/g, ' ')}</option>)}
              </select></label>))}
        </div>
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2"><input type="checkbox" checked={o.bom} onChange={(e) => setO({ ...o, bom: e.target.checked })} data-testid="export-opt-bom" />CSV byte-order mark</label>
          {isAdmin && <label className="flex items-center gap-2"><input type="checkbox" checked={o.full} onChange={(e) => setO({ ...o, full: e.target.checked })} data-testid="export-opt-full" />Full Director IDs (owner/admin, re-confirm password)</label>}
        </div>
        <div className="flex gap-2"><Button variant="outline" onClick={preview} data-testid="export-preview-btn">Preview</Button><Button onClick={download} data-testid="export-download-btn">Download</Button></div>
        {pv && (
          <div className="text-xs space-y-1" data-testid="export-preview">
            <p>{pv.row_count} row(s){pv.masked ? ' · Director IDs masked' : ''}</p>
            {pv.warnings.map((w) => <p key={w} className="text-amber-700 dark:text-amber-300">{w}</p>)}
            <div className="overflow-x-auto"><table className="w-full"><thead><tr>{pv.header.map((h) => <th key={h} className="text-left p-1 border-b border-border">{h}</th>)}</tr></thead>
              <tbody>{pv.rows.map((r, i) => <tr key={i}>{r.map((v, j) => <td key={j} className="p-1 border-b border-border whitespace-nowrap">{v}</td>)}</tr>)}</tbody></table></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
